import tenantRoutes from '@routes';
import tenantTheme from '@theme';
import { Typography } from 'antd';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import TenantComponents from '@components';
import { useRouteNavigate } from '../../../hooks';
import { formatWholeNumber } from '../../../utility/utility';
import { getTimeDateString } from '../../../utility/date';
import {
  canBuyQuota,
  canManageAgencyQuota,
  isAgencyAgent,
  isQuotaAgencyView,
  isSameId,
  isSpecificUserId,
} from '../../../utility/quota';
import { Button, Card, EmptyState, Flex, Group, Icon, Select } from '../../common';
import LoaderWrapper from '../../common/loader-wrapper/loader-wrapper';
import { EmptyCreditUsagePlaceholder } from '../../svg';
import AssignProductsDrawer from '../../assign-products/assign-products-drawer';
import QuotaCreditsStatsWidget from '../QuotaCreditsStatWidget';
import QuotaProductCards from './product-cards';
import QuotaProductsDrawer from './quota-products-drawer';
import { Chip } from './styled';
import { QCSkeleton } from '../credits-quota/credits-quota-skeleton';
import { useGetQuotaCreditsWidgetDataQuery } from '../../../apis/quotaCredits';

const { Text } = Typography;

// Products card for quota users: one tile per product, fed by credits/summary. A tile opens the Available Products
// drawer at that product (a bottom sheet on mobile).
const QuotaProducts = (props) => {
  const { platform, users, usersPermission, cardHeight, bodyPadding, setSelectedUser, user } = props;
  const { t } = useTranslation();
  const navigate = useRouteNavigate();
  // Profolio-Lite (member area): balances only; no usage page, assign or buy actions.
  const { isMobile, isMemberArea } = useSelector((state) => state.app.AppConfig);
  const loggedInUser = useSelector((state) => state.app.loginUser.user);
  const infoDrawerRef = useRef();
  const creditInfoDrawerRef = useRef();
  const productsDrawerRef = useRef();
  // The product the drawer opens at (a tile click); `at` lets the same tile scroll it again.
  const [drawerFocus, setDrawerFocus] = useState(null);
  const [assignVisible, setAssignVisible] = useState(false);

  const canManage = canManageAgencyQuota(loggedInUser);
  const canBuy = canBuyQuota(loggedInUser);
  // Only owners/admins may view one member; an agent asking for user_id gets a 422.
  const viewUserId =
    canManage && isSpecificUserId(user?.id) && !isSameId(user?.id, loggedInUser?.id) ? user?.id : undefined;

  const {
    data,
    isLoading: loading,
    isFetching: fetching,
    error,
    refetch,
  } = useGetQuotaCreditsWidgetDataQuery(
    { ...(viewUserId && { 'q[user_id_eq]': viewUserId }), platform_id: [platform?.id] },
    { skip: !loggedInUser?.id, refetchOnMountOrArgChange: true },
  );

  const isAgencyView = isQuotaAgencyView(data);
  const isAgent = isAgencyAgent(loggedInUser);
  const hasNothing = !!data && data?.totals?.total === 0;
  const packageEnd = data?.package?.end_date;

  const goToUsage = () => navigate(tenantRoutes.app()?.credits_usage?.path);
  const goToBuy = () => navigate(tenantRoutes.app('', false, loggedInUser)?.prop_shop?.path);
  const openAssign = () => {
    productsDrawerRef.current?.closeDrawer();
    setAssignVisible(true);
  };

  const renderUserSelect = () =>
    !!usersPermission && (
      <Select
        size="small"
        horizontal
        value={user?.id}
        options={users}
        onChange={(value, op) => setSelectedUser(op)}
        getOptionLabel={(op) => `${op?.name} ${op?.id === loggedInUser?.id ? t('(Me)') : ''}`}
        inputLoading={loading}
        popupMatchSelectWidth={false}
        accentColor={platform?.brandColor}
      />
    );

  const packageChip = !!data?.package?.name && (
    <Chip>
      <Icon icon="BsBoxSeam" size="14px" color={tenantTheme['primary-color']} />
      <b>{t(data.package.name)}</b>
      {!!packageEnd && `· ${t('ends {{date}}', { date: getTimeDateString(packageEnd, undefined, true) })}`}
    </Chip>
  );

  const learnMore = (
    <Button
      type="link"
      className="p-0"
      style={{ height: 'auto', fontSize: 13, fontWeight: 600, lineHeight: '15px', gap: 2, border: 0 }}
      onClick={() => infoDrawerRef.current?.openDrawer(true)}
    >
      {t('Learn More')}
      <Icon icon="FiChevronRight" size="14px" className="flipX" />
    </Button>
  );

  const footerNote =
    isAgencyView && data?.totals?.notAssigned != null ? (
      <Flex align="center" gap="6px" style={{ color: tenantTheme['text-color-secondary'] }}>
        <Icon icon="FiUsers" size="16px" />
        <span>
          <b style={{ color: tenantTheme['text-color'] }}>{formatWholeNumber(data?.totals?.notAssigned)}</b>{' '}
          {t('not assigned to agents yet')}
        </span>
      </Flex>
    ) : !isAgencyView && !!loggedInUser?.agency && !hasNothing ? (
      <Text type="secondary">{t('Assigned to you by your agency')}</Text>
    ) : null;

  const usageButton = (
    <Button type="default" icon="PiClockClockwiseFill" onClick={goToUsage} className={isMobile ? 'w-100' : undefined}>
      {t('View Usage')}
    </Button>
  );
  const assignButton = canManage && (
    <Button
      type="default"
      icon="FiUsers"
      onClick={() => setAssignVisible(true)}
      className={isMobile ? 'w-100' : undefined}
    >
      {t('Assign to Staff')}
    </Button>
  );
  const buyButton = canBuy && (
    <Button type="primary" icon="IoAddCircleOutline" onClick={goToBuy} className={isMobile ? 'w-100' : undefined}>
      {t('Buy More')}
    </Button>
  );

  // Desktop: the note on the left and the buttons on the right. Mobile: the note, Buy More across the width, then
  // View Usage and Assign to Staff side by side.
  const renderFooter = () =>
    isMobile ? (
      <Flex vertical gap="10px">
        {footerNote}
        {!isMemberArea && (
          <>
            {buyButton}
            <Flex gap="10px">
              {usageButton}
              {assignButton}
            </Flex>
          </>
        )}
      </Flex>
    ) : (
      <Flex align="center" justify="space-between" gap="12px" wrap="wrap" style={{ paddingTop: 4 }}>
        {footerNote || <span />}
        {!isMemberArea && (
          <Flex gap="10px" wrap="wrap">
            {usageButton}
            {assignButton}
            {buyButton}
          </Flex>
        )}
      </Flex>
    );

  // Lite, user with nothing (no package, no units), e.g. a staff member removed from their agency: the plain
  // Credits Balance card at zero, as a Lite credits user sees it. The full dashboard shows the empty state instead.
  if (hasNothing && isMemberArea) {
    return (
      <Card
        style={{ minHeight: cardHeight || (isMobile && 146), '--card-align': 'start', '--flex-direction': 'row' }}
        bodyStyle={{ padding: bodyPadding || (isMobile ? 16 : 24) }}
        headStyle={{ borderBottom: 'none' }}
        accentColor={platform?.brandColor}
        title={
          <Flex align="center" gap="6px">
            <span className={isMobile ? 'fz-14 fw-700' : 'fz-16 fw-700'}>{t('Credits Balance')}</span>
            <Button
              onClick={() => creditInfoDrawerRef.current?.openDrawer(true)}
              shape="circle"
              icon="BsInfoLg"
              type="primary-light"
              iconSize="14px"
              aria-label={t('Credits Balance')}
              style={{ '--btn-bg-color': tenantTheme['primary-light-3'], height: '18px', width: '18px' }}
              iconColor={tenantTheme['primary-color']}
              size="small"
              className="btn-icon-size"
            />
            <TenantComponents.CreditInfoDrawer ref={creditInfoDrawerRef} />
          </Flex>
        }
      >
        <QuotaCreditsStatsWidget
          available={0}
          used={0}
          total={0}
          brandColor={platform?.brandColor}
          platform={platform}
        />
      </Card>
    );
  }

  return error ? (
    <EmptyState message={error} onClick={refetch} buttonLoading={loading} />
  ) : (
    <div>
      <Card
        style={{ minHeight: cardHeight, '--card-align': 'start', '--flex-direction': 'row' }}
        bodyStyle={{ padding: bodyPadding || (isMobile ? 16 : 24), paddingTop: isMobile ? 16 : 0 }}
        headStyle={{ borderBottom: 'none' }}
        accentColor={platform?.brandColor}
        loading={!!loading && !!fetching}
        // Mobile builds its own header in the body (title + Learn More on one line, the package below) so nothing
        // wraps or crowds; desktop keeps the card header.
        title={
          !isMobile && (
            <Flex align="center" gap="12px">
              <span className="fz-16 fw-700">{t('Available Products')}</span>
              {learnMore}
            </Flex>
          )
        }
        extra={
          !isMobile && (
            <Flex align="center" gap="10px">
              {packageChip}
              {renderUserSelect()}
            </Flex>
          )
        }
      >
        <TenantComponents.QuotaInfoDrawer ref={infoDrawerRef} />
        {!!loading && !!fetching ? (
          <QCSkeleton isMobile={isMobile} />
        ) : (
          <Group template="initial" gap="14px">
            {isMobile && (
              <Flex vertical gap="8px">
                <Flex align="center" justify="space-between" gap="8px">
                  <Flex align="center" gap="10px" style={{ minWidth: 0 }}>
                    <span className="fz-16 fw-700" style={{ whiteSpace: 'nowrap' }}>
                      {t('Available Products')}
                    </span>
                    {learnMore}
                  </Flex>
                  {renderUserSelect()}
                </Flex>
                {packageChip && <div>{packageChip}</div>}
              </Flex>
            )}
            {hasNothing ? (
              <EmptyState
                type="quota-credits-dashboard"
                illustration={<EmptyCreditUsagePlaceholder />}
                title={t('No products yet')}
                message={
                  canBuy
                    ? t('Buy a package or top up to get started.')
                    : t('No products are assigned to you yet. Ask your agency admin.')
                }
                hideRetryButton
              />
            ) : (
              <LoaderWrapper loading={!loading && !!fetching}>
                <QuotaProductCards
                  data={data}
                  isAgencyView={isAgencyView}
                  isAgent={isAgent}
                  rail={isMobile}
                  onOpen={(slug) => {
                    setDrawerFocus({ slug, at: Date.now() });
                    productsDrawerRef.current?.openDrawer();
                  }}
                />
              </LoaderWrapper>
            )}
            {renderFooter()}
          </Group>
        )}
      </Card>
      <QuotaProductsDrawer
        ref={productsDrawerRef}
        data={data}
        focus={drawerFocus}
        isAgencyView={isAgencyView}
        isAgent={isAgent}
        canManage={canManage && !isMemberArea}
        canBuy={canBuy && !isMemberArea}
        onAssign={openAssign}
        onBuy={goToBuy}
      />
      {canManage && !isMemberArea && <AssignProductsDrawer isVisible={assignVisible} setIsVisible={setAssignVisible} />}
    </div>
  );
};

export default QuotaProducts;
